<template>
	<div>
		<div class="mb-4">
			<div class="page-title">
				<h2>Netflix Tool - Settings</h2>
				<div class="ml-auto">
					<a @click="saveSettings" class="btn btn-md btn-success"><i class="fas fa-save"></i> Save Settings</a>
				</div>
			</div>
			<div class="flex box start">
				<div class="form-group six-lg">
					<label for="selectedProviderList">Provider List</label>
					<select v-model="data.settings.selectedProviderList" id="selectedProviderList">
						<option v-for="list in orderedProviderLists" :value="list.id">{{ list.name }} ({{ list.description }})</option>
					</select>
				</div>
				<div class="form-group six-lg">
					<label for="summarySettings">Summary Settings</label>
					<select id="summarySettings" v-model="data.settings.selectedSummarySetting">
						<option v-for="list in orderedSummarySettings" :value="list.id">{{ list.name }}</option>
					</select>
				</div>
				<div class="form-group twelve-lg">
					<label for="netflix-gototag">Default goto tag</label>
					<input id="netflix-gototag" v-model="data.settings.goto_default" type="text">
				</div>
			</div>
		</div>
		<list-renderer :apiUrl="'/api/' + selectedSite.slug + '/netflix/getalltitles'" :filterIn="filterIn" :listInData="'titles'" :additionalFilters="additionalFilters">
			<template #header="{title}">
				<h2>Netflix Tool - Title Editor</h2>
				<div class="ml-auto">
					<a @click="showModal()" class="btn btn-md btn-success"><i class="fas fa-plus"></i> Add New Title</a>
				</div>
			</template>
			<template #content='{list,rawData}'>

				<div class="table-responsive">
					<table>
						<thead>
							<tr>
								<th>ID</th>
								<th>Title</th>
								<th>Customized</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="title in list">
								<td>{{ title.netflix_id }}</td>
								<td>{{ title.title }}</td>
								<td v-if="title.customized">Yes</td>
								<td v-else>No</td>
								<td class="nowrap">
									<a @click="showModal(title)" class="btn btn-icon btn-primary" title="Edit" v-show="userRoles.includes('providers.update')"><i class="fas fa-pencil-alt"></i></a>
									<a @click="deleteTitle(title.id)" class="btn btn-icon btn-danger" title="Delete" v-show="userRoles.includes('providers.update')"><i class="fas fa-trash"></i></a>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</template>
		</list-renderer>


		<modal @close="closeModal" v-show="isModalVisible">
			<template slot="header">
				<h4 class="modal-title" v-show="currentTitle.id === 0">Add New Title</h4>
				<h4 class="modal-title" v-show="currentTitle.id !== 0">Edit Title</h4>
			</template>
			<template slot="body">

				<div class="flex box">
					<div class="eight-lg">
						<div class="card">
							<header class="card-header">
								<h4 class="card-title">General</h4>
							</header>
							<div class="card-body flex box start">
								<div class="form-group three-lg">
									<label for="netflix_id">ID</label>
									<input @keyup="numbersOnly" id="netflix-netflix_id" v-model="currentTitle.netflix_id" type="text">
									<p class="input-error" v-show="error_messages.numbersonly !== ''">{{ error_messages.numbersonly }}</p>
								</div>
								<div class="form-group nine-lg">
									<label for="netflix-title">Title</label>
									<input id="netflix-title" v-model="currentTitle.title" type="text">
									<p class="input-error" v-show="error_messages.title !== ''">{{ error_messages.title }}</p>
								</div>
								<div class="form-group twelve-lg">
									<label for="netflix-seo-title">SEO Title</label>
									<input id="netflix-seo-title" v-model="currentTitle.data.seo.seotitle" type="text">
									<p class="input-error" v-show="error_messages.seotitle !== ''">{{ error_messages.seotitle }}</p>
								</div>
								<div class="form-group twelve-lg">
									<label for="netflix-seo-description">SEO Description</label>
									<input id="netflix-seo-description" v-model="currentTitle.data.seo.description" type="text">
									<p class="input-error" v-show="error_messages.seodescription !== ''">{{ error_messages.seodescription }}</p>
								</div>
							</div>
						</div>
					</div>
					<div class="four-lg">
						<div class="card">
							<header class="card-header">
								<h4 class="card-title">Extra</h4>
							</header>
							<div class="card-body">
								<label class="label-checkbox" for="seo-indexfollow">
									<input id="seo-indexfollow" type="checkbox" v-model="currentTitle.data.seo.indexfollow" @change="indexFollowChanger('index')">
									Index, Follow
								</label>
								<p class="input-error" v-show="error_messages.noindexnofollow !== ''">{{ error_messages.noindexnofollow }}</p>
								<label class="label-checkbox" for="seo-noindexnofollow">
									<input id="seo-noindexnofollow" type="checkbox" v-model="currentTitle.data.seo.noindexnofollow" @change="indexFollowChanger('noindex')">
									Noindex, Nofollow
								</label>
								<p class=" input-error" v-show="error_messages.noindexnofollow !== ''">{{ error_messages.noindexnofollow }}</p>
							</div>
						</div>
					</div>
				</div>
				<div class="flex box">
					<div class="twelve-lg">
						<div class="card">
							<header class="card-header language-list">
								<h4 class="card-title">Summary</h4>
								<nav>
									<ul class="tabs" v-if="languages">
										<li :class="index === selectedLanguage ? 'active' : ''" @click="setLanguage(index)" v-for="(lang, index) in languages">
											<a href="#">
												<img :alt="lang.title" :src="lang.flagIconUrl" :title="lang.title">
												{{ lang.title }}
											</a>
										</li>
									</ul>
								</nav>
							</header>
							<div class="card-body">
								<div class="form-group">
									<editor
											:api-key="tinyMCEApiKey"
											:init="tinyMCEConfig"
											v-model="currentTitle.translated[languages[selectedLanguage].locale].content"
									/>
								</div>
								<p class="input-error" v-show="error_messages.summary !== ''">{{ error_messages.summary }}</p>
							</div>
						</div>
					</div>
				</div>
			</template>
			<template slot="footer">
				<a @click="closeModal" class="btn btn-md btn-primary modal-close"><i class="fas fa-times"></i> Cancel</a>
				<a @click="saveTitle" class="btn btn-md btn-success" v-show="currentTitle.id === 0"><i class="fas fa-save"></i> Add New Title</a>
				<a @click="saveTitle" class="btn btn-md btn-success" v-show="currentTitle.id !== 0"><i class="fas fa-save"></i> Save Changes</a>
			</template>
		</modal>
	</div>
</template>

<script>
import modal from '../../Modal/DefaultModal.vue';
import Errors from '../../../Utils/Errors.js';
import VueSelect from 'vue-select';
import {eventBus} from '../../../app';


export default {
	props: {
		data: {
			type: Object
		},
	},
	components: {
		modal,
		"vue-select": VueSelect,
	},
	data() {
		return {

			id: this.data.id,

			modules: [
				'content', 'recommendedarticles'
			],
			filterIn: ['title', 'netflix_id'],
			selectedSite: this.$selectedSite,

			languages: this.data.languages,
			selectedLanguage: 0,
			activeTab: 'content',

			userRoles: this.$currentUserRoles,

			isModalVisible: false,

			errors: new Errors(),

			loadCounter: 0,

			currentTitle: {
				data: {
					seo: {},
				},
			},

			error_messages: {},

			validation: true,

			tinyMCEApiKey: this.$tinyMCEApiKey,
			tinyMCEConfig: this.$tinyMCEConfig,

			providerLists: {},
			fetchedData: {},

			metaVisible: false,

			additionalFilters: {
				Customized: {
					elements: [true, false],
					elementsValue: null,
					elementsLabel: null,
					width: 2,
					filterIn: 'customized',
					default: null,
				},
			},
		}
	},
	created() {
		let vm = this;
		this.checkDefaultFields();
		vm.resetErrorMessages();
		this.setLanguage(0);
		this.loadProviderLists();

		eventBus.$on('listRendererData', function (data) {
			vm.fetchedData = data;
		});
	},
	mounted() {
		this.setLanguage(0);
	},
	computed: {
		orderedProviderLists: function () {
			return _.orderBy(this.providerLists, [list => list['name'].toLowerCase()], ['asc']);
		},
		orderedSummarySettings: function () {
			return _.orderBy(this.fetchedData.summerySettings, [setting => setting['name'].toLowerCase()], ['asc']);
		}
	},
	methods: {
		checkDefaultFields: function () {
			if (!this.data.settings.hasOwnProperty('seo')) {
				this.data.settings.seo = {};
			}
		},
		checkDefaultFieldsForCurrentTitle: function () {

			if (!this.currentTitle.hasOwnProperty('translated')) {
				this.currentTitle.translated = {};
			}
			if (!this.currentTitle.hasOwnProperty('data')) {
				this.currentTitle.data = {};
			}
			if (!this.currentTitle.data.hasOwnProperty('seo')) {
				this.currentTitle.data.seo = {
					indexfollow: true,
					noindexnofollow: false,
				};
			}

			if (this.currentTitle.data.seo.hasOwnProperty('noindexnofollow') && !this.currentTitle.data.seo.hasOwnProperty('indexfollow')) {
				this.currentTitle.data.seo.indexfollow = !this.currentTitle.data.seo.noindexnofollow;
			}
			if (this.currentTitle.data.seo.hasOwnProperty('indexfollow') && !this.currentTitle.data.seo.hasOwnProperty('noindexnofollow')) {
				this.currentTitle.data.seo.noindexnofollow = !this.currentTitle.data.seo.indexfollow;
			}
			if (!this.currentTitle.data.seo.hasOwnProperty('indexfollow')) {
				this.currentTitle.data.seo.indexfollow = true;
			}
			if (!this.currentTitle.data.seo.hasOwnProperty('noindexnofollow')) {
				this.currentTitle.data.seo.noindexnofollow = false;
			}
		},
		toggleMetas: function () {
			this.metaVisible = !this.metaVisible;
		},
		changeTab(name) {
			this.activeTab = name;
		},
		showModal: function (title = null) {
			if (title !== null) {
				this.currentTitle = title;
			} else {
				this.currentTitle = {
					title: '',
					id: 0,
					netflix_id: '',
				};
			}

			this.checkDefaultFieldsForCurrentTitle();


			this.setLanguage(0);
			this.isModalVisible = true;
			this.$forceUpdate();
		},
		closeModal: function () {
			eventBus.$emit('listRenderer-fetch');
			this.isModalVisible = false;
		},
		setLanguage: function (index) {
			let vm = this;
			this.selectedLanguage = index;

			//for countries
			if (typeof this.currentTitle.translated === 'undefined') {
				this.currentTitle.translated = {};
			}
			if (typeof this.currentTitle.translated[this.languages[this.selectedLanguage].locale] === 'undefined') {
				this.currentTitle.translated[this.languages[this.selectedLanguage].locale] = {};
				this.currentTitle.translated[this.languages[this.selectedLanguage].locale].content = '';
			}
		},
		indexFollowChanger: function (state) {
			if (state === 'index') {
				this.currentTitle.data.seo.noindexnofollow = !this.currentTitle.data.seo.indexfollow;
			}
			if (state === 'noindex') {
				this.currentTitle.data.seo.indexfollow = !this.currentTitle.data.seo.noindexnofollow;
			}
			this.$forceUpdate();
		},
		loadProviderLists: function () {
			let vm = this;

			axios.get('/api/' + vm.$selectedSite.slug + '/providerlists/getall',
					{},
			).then(function (response) {
				vm.providerLists = response.data.providerList;

			}).catch(function (e) {
				vm.errors.record(e.response.data);
			});
		},


		saveTitle: function () {
			let vm = this;

			if (this.validator() !== true) {
				return true;
			}

			let apiUrl = '/api/' + vm.$selectedSite.slug + '/netflix/update/' + vm.currentTitle.id;
			if (vm.currentTitle.id === 0) {
				apiUrl = '/api/' + vm.$selectedSite.slug + '/netflix/store';
			}

			axios.post(apiUrl,
					{
						title: vm.currentTitle
					},
			).then(function (response) {
				if (response.data.success === true) {
					vm.$toastr.success('Title saved successfully.');
					vm.closeModal();
				} else {
					vm.$toastr.error(response.data.error, 'Error');
				}

			}).catch(function (e) {
				vm.$toastr.error('Title saving failed.', 'Error');
				vm.errors.record(e.response.data);
			});
		},
		deleteTitle: function (id) {
			let vm = this;

			vm.$dialog.confirm('Are you sure you want to delete this Title?', {
				loader: true
			})
					.then((dialog) => {
						axios.post('/api/' + vm.$selectedSite.slug + '/netflix/delete/' + id,
								{},
						).then(function (response) {
							if (response.data.success === true) {
								vm.$toastr.success('Title deleted successfully.');
								vm.closeModal();
								dialog.close();
							} else {
								vm.$toastr.error(response.data.error, 'Error');
							}


						}).catch(function (e) {
							vm.$toastr.error('Title delete failed.', 'Error');
							vm.errors.record(e.response.data);
						});
					});
		},
		getParameterByName: function (name, url) {
			if (!url) url = window.location.href;
			name = name.replace(/[\[\]]/g, '\\$&');
			var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
					results = regex.exec(url);
			if (!results) return null;
			if (!results[2]) return '';
			return decodeURIComponent(results[2].replace(/\+/g, ' '));
		},
		resetErrorMessages: function () {
			this.error_messages = {
				country_id: '',
				article_id: '',
				providerlist_id: '',
				summary: '',
				numbersonly: '',
			};
		},
		numbersOnly: function () {
			this.error_messages.numbersonly = '';
			const regex = /^-?\d+\.?\d*$/;
			if (!regex.test(this.currentTitle.netflix_id)) {
				this.error_messages.numbersonly = 'only number allowed here';
				this.validation = false;
			}
		},
		validator: function () {
			this.resetErrorMessages();
			this.validation = true;
			let summaryValidation = 0;

			if (this.currentTitle.title === '') {
				this.error_messages.title = 'Enter a title';
				this.validation = false;
			}

			$.each(this.currentTitle.translated, function (key, langBlock) {
				if (langBlock.content !== '') {
					summaryValidation++
				}
			});
			if (summaryValidation === 0) {
				//can save blank summary
				//this.error_messages.summary = 'please fill in';
				//this.validation = false;
			}

			const regex = /^-?\d+\.?\d*$/;
			if (!regex.test(this.currentTitle.netflix_id)) {
				this.error_messages.numbersonly = 'only number allowed here';
				this.validation = false;
			}
			return this.validation;
		},
		saveSettings: function () {
			let vm = this;
			let apiUrl = '/api/' + vm.$selectedSite.slug + '/staticpage/' + vm.data.id + '/savesettings';
			axios.post(apiUrl,
					{
						settings: vm.data.settings
					},
			).then(function (response) {
				if (response.data.success === true) {
					vm.$toastr.success('Settings saved successfully.');
					vm.closeModal();
				} else {
					vm.$toastr.error(response.data.error, 'Error');
				}

			}).catch(function (e) {
				vm.$toastr.error('Settings save failed.', 'Error');
				vm.errors.record(e.response.data);
			});
		},

	}
}
</script>

